/**
 * Idle limit countdowns (host option, see `IDLE_LIMITS`): the server sends each timed player's
 * time left (`PublicPlayer.idleMs`) with every view; once `IDLE_WARNING_MS` or less is left the
 * table counts it down, on their seat and, for you, as a banner.
 */
import { useEffect, useState } from 'react';
import { IDLE_WARNING_MS, t, type GameView, type PlayerId } from '@kadice/shared';

/** Players whose countdown is showing → whole seconds left, counting down each second until the next view. */
export function useIdleWarnings(view: GameView): Map<PlayerId, number> {
  const [tick, setTick] = useState<{ view: GameView; elapsed: number } | null>(null);
  const timed = view.players.some((p) => p.idleMs !== null);
  useEffect(() => {
    if (!timed) return;
    const at = Date.now();
    const id = setInterval(() => setTick({ view, elapsed: Date.now() - at }), 500);
    return () => clearInterval(id);
  }, [view, timed]);
  const elapsed = tick?.view === view ? tick.elapsed : 0;
  const out = new Map<PlayerId, number>();
  for (const p of view.players) {
    if (p.idleMs === null) continue;
    const left = p.idleMs - elapsed;
    if (left <= IDLE_WARNING_MS) out.set(p.id, Math.max(0, Math.ceil(left / 1000)));
  }
  return out;
}

/** On a seat's name: seconds until this player forfeits and watches. */
export function IdlePill({ seconds }: { seconds: number }) {
  return (
    <span className="idle-pill" title={t('idle.seatTitle', { n: seconds })}>
      {t('idle.seconds', { n: seconds })}
    </span>
  );
}

/** You: make your move or you forfeit and watch. */
export function IdleBanner({ seconds }: { seconds: number }) {
  return (
    <div className="idle-banner" role="alert">
      <strong>{t('idle.title')}</strong>
      <span>{t('idle.body', { n: seconds })}</span>
    </div>
  );
}
